import { Controller } from "@hotwired/stimulus";
import { pronounce } from "../components/lib/pronunciation";

export default class extends Controller {
  static targets = [
    "popover", "loading", "error", "data",
    "word", "translation", "transcription", "pos", "examples",
    "listenButton", "saveButton"
  ];

  static values = {
    studyClassId: String,
    lessonId: String,
    pronunciationLang: String
  };

  connect() {
    this._onSelectionChange = this._onSelectionChange.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    document.addEventListener("selectionchange", this._onSelectionChange);
    document.addEventListener("pointerdown", this._onPointerDown);
    this._abortController = null;
    this._currentEntry = null;
  }

  disconnect() {
    document.removeEventListener("selectionchange", this._onSelectionChange);
    document.removeEventListener("pointerdown", this._onPointerDown);
    this._cancelInFlight();
  }

  _onPointerDown(event) {
    if (this.hasPopoverTarget && this.popoverTarget.contains(event.target)) return;
    this._hide();
  }

  _onSelectionChange() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      this._hide();
      return;
    }

    const text = selection.toString().trim();
    if (!text) { this._hide(); return; }

    // Single word only — no whitespace, no punctuation runs.
    if (/\s/.test(text)) { this._hide(); return; }
    if (text.split(/\s+/).length !== 1) { this._hide(); return; }

    const range = selection.getRangeAt(0);
    if (!this._isWithinElement(range)) { this._hide(); return; }
    if (this._isInsideEditable(range)) { this._hide(); return; }

    this._show(text, range);
  }

  _isWithinElement(range) {
    return this.element.contains(range.startContainer) && this.element.contains(range.endContainer);
  }

  _isInsideEditable(range) {
    let node = range.startContainer;
    if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
    while (node && node !== this.element) {
      const tag = node.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return true;
      if (node.isContentEditable) return true;
      node = node.parentElement;
    }
    return false;
  }

  _show(text, range) {
    this._cancelInFlight();
    this._currentText = text;
    this._currentRange = range.cloneRange();
    this._positionNear(range);
    this.popoverTarget.classList.remove("hidden");
    this._setState("loading");
    this._fetchLookup(text);
  }

  _hide() {
    if (!this.hasPopoverTarget) return;
    this.popoverTarget.classList.add("hidden");
    this._cancelInFlight();
    this._currentEntry = null;
    this._currentText = null;
  }

  _setState(state) {
    if (this.hasLoadingTarget) this.loadingTarget.classList.toggle("hidden", state !== "loading");
    if (this.hasErrorTarget) this.errorTarget.classList.toggle("hidden", state !== "error");
    if (this.hasDataTarget) this.dataTarget.classList.toggle("hidden", state !== "data");
  }

  _cancelInFlight() {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
  }

  _positionNear(range) {
    const rect = range.getBoundingClientRect();
    const popover = this.popoverTarget;
    popover.style.position = "absolute";
    popover.style.top = `${window.scrollY + rect.bottom + 8}px`;
    popover.style.left = `${window.scrollX + rect.left}px`;
  }

  async _fetchLookup(text) {
    this._abortController = new AbortController();
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch("/translations/lookup", {
        method: "POST",
        signal: this._abortController.signal,
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrf || ""
        },
        body: JSON.stringify({
          text,
          study_class_id: this.studyClassIdValue,
          lesson_id: this.lessonIdValue
        })
      });
      if (!response.ok) throw new Error(`lookup ${response.status}`);
      const data = await response.json();
      this._renderData(text, data);
    } catch (e) {
      if (e.name === "AbortError") return;
      this._setState("error");
    }
  }

  _renderData(text, data) {
    this._currentEntry = { text, ...data };
    if (this.hasWordTarget) this.wordTarget.textContent = text;
    if (this.hasTranslationTarget) this.translationTarget.textContent = data.translation || "";

    if (this.hasTranscriptionTarget) {
      this.transcriptionTarget.textContent = data.transcription || "";
      this.transcriptionTarget.classList.toggle("hidden", !data.transcription);
    }
    if (this.hasPosTarget) {
      this.posTarget.textContent = data.pos || "";
      this.posTarget.classList.toggle("hidden", !data.pos);
    }
    if (this.hasExamplesTarget) {
      this.examplesTarget.innerHTML = "";
      const examples = data.examples || [];
      examples.slice(0, 3).forEach(ex => {
        const li = document.createElement("li");
        li.textContent = ex;
        this.examplesTarget.appendChild(li);
      });
      this.examplesTarget.classList.toggle("hidden", !examples.length);
    }
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = false;
      this.saveButtonTarget.textContent = this.saveButtonTarget.dataset.defaultLabel || this.saveButtonTarget.textContent;
    }
    this._setState("data");
  }

  async listen() {
    if (!this._currentEntry) return;
    const button = this.listenButtonTarget;
    button.disabled = true;
    try {
      await pronounce({ text: this._currentEntry.text, lang: this.pronunciationLangValue });
    } finally {
      button.disabled = false;
    }
  }

  async save() {
    if (!this._currentEntry) return;
    const button = this.saveButtonTarget;
    const originalLabel = button.dataset.defaultLabel || button.textContent;
    button.dataset.defaultLabel = originalLabel;
    button.disabled = true;
    try {
      const sentence = this._captureSentence();
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch("/vocabulary", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrf || ""
        },
        body: JSON.stringify({
          word: this._currentEntry.text,
          study_class_id: this.studyClassIdValue,
          lesson_id: this.lessonIdValue,
          translation: this._currentEntry.translation,
          transcription: this._currentEntry.transcription,
          pos: this._currentEntry.pos,
          context_sentence: sentence,
          definitions: this._currentEntry.definitions || [],
          examples: this._currentEntry.examples || []
        })
      });
      if (!response.ok) throw new Error(`save ${response.status}`);
      button.textContent = button.dataset.savedLabel || originalLabel;
      const selection = window.getSelection();
      if (selection) selection.removeAllRanges();
      this._hide();
      button.disabled = false;
      button.textContent = originalLabel;
    } catch (e) {
      button.disabled = false;
      button.textContent = button.dataset.errorLabel || originalLabel;
    }
  }

  _captureSentence() {
    if (!this._currentRange) return null;
    let node = this._currentRange.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return null;
    const text = node.textContent || "";
    const offset = this._currentRange.startOffset;

    const before = text.slice(0, offset);
    const after = text.slice(offset);

    const startMatch = before.match(/[.!?][^.!?]*$/);
    const start = startMatch ? offset - (startMatch[0].length - 1) : 0;
    const endMatch = after.match(/[.!?]/);
    const end = endMatch ? offset + endMatch.index + 1 : text.length;

    return text.slice(start, end).trim();
  }
}
